import React, { useMemo, useRef, useState } from "react";


// ---- color helpers ----
const hex2rgb = (hex) => {
  const s = hex.replace("#", "");
  const v =
    s.length === 3
      ? s.split("").map((ch) => parseInt(ch + ch, 16))
      : [s.slice(0, 2), s.slice(2, 4), s.slice(4, 6)].map((h) =>
          parseInt(h, 16)
        );
  return { r: v[0], g: v[1], b: v[2] };
};
const rgb2hex = (r, g, b) => {
  const h = (n) => n.toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
};
const rgbToHls = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return { h, l, s };
};
const hlsToRgb = (h, l, s) => {
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const conv = (t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    r = conv(h + 1 / 3);
    g = conv(h);
    b = conv(h - 1 / 3);
  }
  return {
    r: Math.min(255, Math.max(0, Math.round(r * 255))),
    g: Math.min(255, Math.max(0, Math.round(g * 255))),
    b: Math.min(255, Math.max(0, Math.round(b * 255))),
  };
};

export function HeatTable({
  values,
  rowLabels,
  colLabels,
  colorValTrf = (x) => 0.6 + ((1 - 0.6) / 0.12) * Math.min(x, 0.12),
  colorMaxHex = "#BBD775",
  decimals = 3,
  className = "",
  tableType = "",
}) {
  if (!Array.isArray(values) || values.length === 0) return null;
  const nRows = values.length;
  const nCols = values[0].length;

  // ---- sort state ----
  const originalOrderRef = useRef(Array.from({ length: nRows }, (_, i) => i));
  const [sortBy, setSortBy] = useState(null); // null => original order
  const [sortDir, setSortDir] = useState("dec"); // 'asc' | 'desc'

  const onHeaderClick = (j) => {
    // j === -1 means top-left header (row-label column) -> reset order
    if (j === -1) {
      setSortBy(null);
      return;
    }
    if (sortBy === j) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(j);
      setSortDir("asc"); // first click sorts descending; change if you prefer 'asc'
    }
  };

  const sortedIdx = useMemo(() => {
    if (sortBy == null) return originalOrderRef.current.slice();
    const idx = originalOrderRef.current.slice();
    idx.sort((a, b) => {
      const va = values[a][sortBy];
      const vb = values[b][sortBy];
      const na = Number.isFinite(va) ? va : -Infinity; // push NaN/±Inf cleanly
      const nb = Number.isFinite(vb) ? vb : -Infinity;
      return sortDir === "asc" ? na - nb : nb - na;
    });
    return idx;
  }, [sortBy, sortDir, values,]);

  const { r: rMax, g: gMax, b: bMax } = hex2rgb(colorMaxHex);
  const { h: Hmax, s: Smax } = rgbToHls(rMax, gMax, bMax);
  const toCellColor = (x) => {
    let L = colorValTrf(x);
    if (!Number.isFinite(L)) L = 0.5;
    L = Math.min(1, Math.max(0, L));
    const { r, g, b } = hlsToRgb(Hmax, L, Smax);
    const isBold = tableType === "p" && x <= 0.05;
    return [rgb2hex(r, g, b), isBold];
  };

  const format = (x) => (Number.isFinite(x) ? x.toFixed(decimals) : String(x));

  // ---- styles ----
  const tableStyle = {
    borderCollapse: "collapse",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    fontSize: 14,
  };
  const thBase = {
    position: "sticky",
    top: 0,
    background: "#fff",
    color: "#000",
    textAlign: "left",
    border: "1px solid #c9c9c9",
    padding: "6px 8px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    cursor: "pointer",
  };
  const rowHeaderStyle = {
    position: "sticky",
    left: 0,
    background: "#fff",
    color: "#000",
    textAlign: "left",
    border: "1px solid #c9c9c9",
    padding: "6px 8px",
    fontWeight: 600,
    whiteSpace: "nowrap",
  };
  const tdStyle = {
    border: "1px solid #c9c9c9",
    padding: "6px 8px",
    textAlign: "right",
  };

  const sortIndicator = (j) =>
    sortBy === j ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return (
    <div className={className} style={{ overflow: "auto", maxWidth: "100%" }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            {rowLabels ? (
              <th
                style={{ ...thBase }}
                title="Reset original row order"
                onClick={() => onHeaderClick(-1)}
              >
                {/* top-left header cell */}↺
              </th>
            ) : null}
            {Array.from({ length: nCols }).map((_, j) => (
              <th
                key={`c${j}`}
                style={{ ...thBase }}
                onClick={() => onHeaderClick(j)}
                title="Click to sort"
              >
                {(colLabels ? colLabels[j] : `col ${j + 1}`) + sortIndicator(j)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedIdx.map((ri) => {
            const row = values[ri];
            // console.log(ri, nRows);
            return (
              <tr key={`r${ri}`}>
                {rowLabels ? (
                  <th style={rowHeaderStyle}>{rowLabels[ri]}</th>
                ) : null}
                {row.map((v, j) => {
                  const [bg, isBold] = toCellColor(v);
                  const { r, g, b } = hex2rgb(bg);
                  const luminance =
                    (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
                  const color = luminance > 0.6 ? "#222" : "#fff";
                  return (
                    <td
                      key={`c${ri}-${j}`}
                      style={{
                        ...tdStyle,
                        background: bg,
                        color,
                        fontWeight: isBold ? 500 : "default",
                      }}
                    >
                      {format(v)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
